/**
 * Helpers index
 */
/* eslint import/order: ["warn", {"newlines-between": "always-and-inside-groups"}] */
import {
    getImageInfo,
    getImageWidthHeight,
    getImageAspectRatio,
    getImageOrientation,
    getImageCaption,
    getImageCreditLine,
    getImageCopyright,
    getImageLicense,
    getImageUrl,
    getImageSrc,
    constructImageSrc,
    constructImageSrc2,
    getCacheBustParam
} from '@/helpers/image.js';

import {
    getMasonryWidthHeight,
    getMasonryWidthHeightStyle,
    formatImageSrcParams,
    getMasonryClass
} from '@/helpers/masonry.js';

import {
    getDefaultImageSrc,
    getCoverImageSrc,
    getIngressImageSrc,
    getArticleIngressImageSrc,
    getActorIngressImageSrc,
    getUserProfileImageSrc,
    getFullscreenImageSrc,
    getMetaImageSrc,
    getBackgroundPositionByFocusPoint,
    getBackgroundImageStyle,
    getBannerImageStyle,
    getPaletteColorsBackgroundStyle,
    getImageWidthHeightOptimised,
    getImageWidthHeightOptimisedStyle
} from '@/helpers/image-resources.js';

import {
    toLocaleDateString,
    getFirstDateEdited
} from '@/helpers/datetime.js';


// scheduler is needed to flush promise queue
const scheduler = typeof setImmediate === 'function' ? setImmediate : setTimeout;


/**
 * Site base url.
 */
export const getBaseUrl = function () {
    return process.env.API_URL || (process.client && window.location.origin);
};


/**
 * Map lowercase "_links" resource (eg. artworks) to uppercase singular (eg. Artwork)
 */
export const getResourceKey = function (resource) {
    let key;
    switch (resource) {
    case 'actors':
        key = 'Actor';
        break;
    case 'artworks':
        key = 'Artwork';
        break;
    case 'audios':
        key = 'Audio';
        break;
    case 'collections':
        key = 'Collection';
        break;
    case 'events':
        key = 'Event';
        break;
    case 'featurearticles':
        key = 'FeatureArticle';
        break;
    case 'organizations':
        key = 'Organization';
        break;
    case 'videos':
        key = 'Video';
        break;
    }
    return key;
};


/**
 * Detects the resource type (ResourcePath) of item.
 * (actors, artworks, collections etc)
 */
export const getItemResourceType = function (item) {
    if (!item) {
        return;
    }
    else if (item.ResourcePath) {
        return item.ResourcePath;
    }
    // the rest is to support resources without ResourcePath (there should not be any)
    let resource;
    if (item._links && item._links.self) {
        const href = item._links && item._links.self && item._links.self.href;
        const match = href && href.match(/\/svc\/(.*)\//);
        resource = match && match[1];
    }
    if (item.ActorType) {
        resource = 'actors';
    }
    if (item.Makers || item.MakersShort) {
        resource = 'artworks';
    }
    return resource;
};


/**
 * Return the card component (artwork-card, etc) for item by type (ResourcePath).
 */
export const getItemCardType = function (item) {
    const resourceType = getItemResourceType(item);
    switch (resourceType) {
    case 'actors':
        return 'actor-card';
    case 'artworks':
        return 'artwork-card';
    case 'audios':
        return 'audio-card';
    case 'collections':
        return 'collection-card';
    case 'events':
        return 'event-card';
    case 'exhibitions':
        return 'event-card';
    case 'featurearticles':
        return 'feature-article-card';
    case 'organizations':
        return 'organization-card';
    case 'videos':
        return 'video-card';
    default:
        console.warn("Unknown resource type", resourceType);
        return '';
    }
};


/**
* Return Actors as string.
*/
export const getMakersShort = function (item) {
    if (item && item.MakersShort) {
        return item.MakersShort;
    }
    else if (item && item.Makers) {
        return _.map(item.Makers, 'Name').join(' & ');
    }
    else {
        return '';
    }
};


/**
* Return Actor lifetime as string ("BirthYear – " or "BirthYear – DeathYear")
*/
export const getActorLifetime = function (actor) {
    if (!actor || !actor.BirthYear || actor.BirthYear === 0) {
        return "";
    }
    let years = actor.BirthYear + ' – ';
    if (actor.DeathYear) {
        years += actor.DeathYear;
    }
    return years;
};


/**
 * Return highest bitrate video url from given `videoInfo`
 */
export const getEncodedVideoUrl = function (videoInfo) {
    // here are various resolutions, take the first (probably with highest bitrate)
    const video = videoInfo.EncodedVideos && _.head(videoInfo.EncodedVideos);

    // ensure the source protocol is https
    return video && video.Url && video.Url.replace(/^http:/, 'https:');
};


/**
 * Cleanup query Text param.
 * Remove * and ?, replace - with space
 */
export const cleanupQueryText = function (text) {
    if (!text) {
        return '';
    }
    return text
        .replace(/\*/g, '')
        .replace(/\?/g, '')
        .replace(/-/g, ' ');
};


/**
 * Artwork geometric dimensions string formatter
 */
export const stringifyDimension = function (dimension) {
    return `${dimension.Type} ${dimension.Value} ${dimension.Unit}`;
};


/**
 * FileLibrary upload url.
 */
export const getFileLibraryUploadUrl = function (fileLibrary) {
    return fileLibrary && fileLibrary.Id &&
        `${getBaseUrl()}/svc/filelibraries/${fileLibrary.Id}`;
};


export const filterByImageUrl = function (items) {
    return _.filter(items, (item) => {
        const src = getImageUrl(item);
        return (typeof src === 'string' && src.length > 0);
    });
};


/**
** Set and return domain cookie
**/
export const setCookie = function (cname, cvalue, exMins) {
    if (!process.client) {
        return;
    }

    const d = new Date();
    d.setTime(d.getTime() + (exMins * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    const domain = "domain=" + window.location.hostname;
    const cookie = `${cname}=${cvalue};${domain};${expires};path=/`;

    document.cookie = cookie;
};


/**
 * Full locale with territory.
 */
export const getFullLocale = function (locale) {
    switch (locale) {
    case 'en':
        return 'en_US';
    case 'fi':
        return 'fi_FI';
    case 'sv':
        return 'sv_SE';
    default:
        return 'en_US';
    }
};


export const hexToRGB = function (hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
        return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
    }
    else {
        return "rgb(" + r + ", " + g + ", " + b + ")";
    }
};


export const flushPromises = function (timeout) {
    return new Promise((resolve) => {
        scheduler(resolve, timeout || 0);
    });
};


export const logErrorTrace = /* istanbul ignore next */ function (error, showStackTrace) {
    const status = error?.response?.data?.ResponseStatus;

    // open stack group
    if (showStackTrace) {
        console.group((error.message || error.name), (error.isAxiosError && error.config?.url) || '');
    }

    // ResponseStatus.Errors from the backend
    if (status?.Errors) {
        const errors = _.map(status.Errors, (err) => {
            return `${err.ErrorCode}, ${err.FieldName}: ${err.Message}`;
        });
        showStackTrace ?
            console.error(status.Message, errors, status.StackTrace, error.config || '')
            : console.error((error.message || error.name), status.Message, errors);
    }
    // generic error
    else if (error) {
        showStackTrace ?
            console.error(error, error.response?.status || '', error.config || '')
            : console.error((error.message || error.name), error.response?.status, error);
    }

    // end stack group
    if (showStackTrace) {
        error.response && console.info(error.response.data);
        console.groupEnd();
    }
};


export default {

    getBaseUrl,

    getResourceKey,
    getItemResourceType,
    getItemCardType,
    getBackgroundPositionByFocusPoint,
    getBackgroundImageStyle,
    getBannerImageStyle,
    getMakersShort,
    getActorLifetime,
    cleanupQueryText,
    stringifyDimension,
    getFileLibraryUploadUrl,
    getEncodedVideoUrl,
    filterByImageUrl,
    setCookie,
    getFullLocale,
    hexToRGB,
    flushPromises,
    logErrorTrace,

    // imported from image-*
    getImageInfo,
    getImageUrl,
    getImageSrc,
    constructImageSrc,
    constructImageSrc2,
    getFullscreenImageSrc,
    getMetaImageSrc,
    getIngressImageSrc,
    getArticleIngressImageSrc,
    getActorIngressImageSrc,
    getUserProfileImageSrc,
    getCoverImageSrc,
    getDefaultImageSrc,
    getImageWidthHeight,
    getImageWidthHeightOptimised,
    getImageWidthHeightOptimisedStyle,
    getPaletteColorsBackgroundStyle,
    getImageAspectRatio,
    getImageOrientation,
    getImageCaption,
    getImageCreditLine,
    getImageCopyright,
    getImageLicense,
    getCacheBustParam,

    // imported from masonry
    getMasonryWidthHeight,
    getMasonryWidthHeightStyle,
    formatImageSrcParams,
    getMasonryClass,

    // imported from datetime.js
    toLocaleDateString,
    getFirstDateEdited

};
